import {
  docTypeLabel, occupancyLabel, propertyLabel, purposeLabel,
  type DocTypeCode, type LoanPurpose, type Occupancy, type PropertyType,
} from '@lenderhound/shared';
import type { MatrixCell, MatrixGrid, MatrixProse, MatrixSlice } from '../api.ts';

/**
 * The whole grid a cited cell sits in, and the lender's own prose printed beside it.
 *
 * Henry asked for exactly this pairing on 2026-09-01: "I think we show both the guideline
 * paragraph + the whole matrix". The half that was impossible until tonight is the
 * paragraph - matrix documents held grid cells and no text at all, and the ingest run
 * that wrote 2,216 `guideline_section` rows over them is what makes a footnote reachable
 * from a cell. See MatrixSlice in api.ts for what "the whole matrix that applies" is
 * taken to mean, and for the endpoint this is drawn from.
 *
 * ── THE ACCOUNT BOUNDARY, WHICH MOVED ─────────────────────────────────────────────
 *
 * `sourceViewable` is true for every signed-in account from 2026-09-08 (Misha: "for both
 * superuser and 'demo' user and regular user, they should be able to see the matrix PDF
 * stuff"), so in practice both objects below are now drawn for everyone. The argument is
 * kept rather than deleted because the SHAPE is still on the wire - the server can still
 * send `bodyMd: null` with `proseWithheld: true`, this component still renders that as
 * information rather than as a gap, and one line in accounts.ts is all it takes to make
 * it live again. What follows is why the line was drawn where it was.
 *
 * There are two different objects in this panel and they did not fall on the same side of
 * the broker/staff line, so `sourceViewable` gated one of them and not the other.
 *
 * THE CELLS ARE OURS. Every number in the grid is a `matrix_cell` row: our vision pass
 * read the page, our extraction closed the FICO rungs at 850, our band arithmetic turned
 * a column of printed maxima into half-open ranges, and our EXCLUDE constraint is what
 * says the rows do not overlap. None of that is a reproduction of the lender's document;
 * it is our database, drawn as a table. It is also the same class of fact the product
 * already hands a broker in prose - the answer says "Arc caps you at 80% here" and the
 * lender card prints the ceiling - so the grid adds the NEIGHBOURS of a number the
 * broker was already given, which is the entire ask. Brokers get the cells.
 *
 * THE PARAGRAPHS ARE THE LENDER'S. `body_md` is verbatim text lifted whole and unedited -
 * Arc's LTV Matrix Footnotes run to 985 characters of the lender's own clauses, cash-in-
 * hand caps and condotel carve-outs included. Handing that over is materially handing
 * over the page, which is the one thing `canOpenSourceDocuments` exists to refuse. The
 * single validated `quote` on a citation is a different object and stays visible to
 * everyone: it is the minimum needed to make one claim checkable, which is attribution,
 * not redistribution. So the bodies are withheld from brokers - server-side, per the
 * contract in api.ts; this component only declines to draw what it was not sent.
 *
 * What a broker gets INSTEAD is not silence, because silence beside a grid reads as "no
 * fine print" and that would be a lie: they are told the qualifying sections exist, by
 * title, with whether each one narrows or widens the grid, and pointed at the two routes
 * that are actually open to them - the follow-up question, which reads the full text on
 * their behalf, and their AE. Nothing about the underwriting is withheld; the answer
 * above was already written with those footnotes in it.
 *
 * FLAGGED FOR HENRY rather than settled: this is a judgement call about his own
 * documents, and the one that could reasonably go the other way. If lender matrices are
 * material he considers a broker entitled to - and several of these are published on
 * lender websites - then flipping `PROSE_IS_SOURCE_MATERIAL` to false here plus dropping
 * the strip on the server shows brokers the footnotes too. It is deliberately one named
 * constant and not a scattering of `sourceViewable &&`.
 */
const PROSE_IS_SOURCE_MATERIAL = true;

interface Props {
  slice: MatrixSlice;
  /**
   * Whether this account may open source documents - `canOpenSourceDocuments`, resolved
   * once in Results.tsx and handed down through CitationViewer, so this panel and the
   * page renderer beside it cannot disagree about who is staff.
   *
   * It gates two things here and they are separate judgements that happen to land on the
   * same side: the lender's verbatim paragraphs (source material, see above) and the
   * investor name (our supply chain, see Results.tsx). It gates NOTHING about the cells.
   */
  sourceViewable: boolean;
}

/* ------------------------------------------------------------------ formatting */

/**
 * Money at the width a column header can afford. "$1.5M", "$750k", "$250,000".
 *
 * `parseFloat(x.toFixed(2))` rather than toFixed alone: 3.00 must read "3M" and 1.25
 * must keep both digits, and a trailing-zero regex gets 1.20 wrong in the other
 * direction. The exact figure is never lost - it is on the header's title attribute,
 * because a broker sizing a loan against a band boundary needs the real number.
 */
function shortMoney(n: number): string {
  if (n >= 1_000_000) return `$${parseFloat((n / 1_000_000).toFixed(2))}M`;
  if (n >= 1_000) return `$${parseFloat((n / 1_000).toFixed(1))}k`;
  return `$${n.toLocaleString()}`;
}

const exactMoney = (n: number): string => `$${n.toLocaleString()}`;

/**
 * A loan band, half-open on the low side: (min, max].
 *
 * The zero-floor band is the common case and reads "≤ $1.5M" rather than "$0–$1.5M",
 * which is how the lender prints it. Every other band shows both ends; the exclusive
 * lower bound is spelled out in the tooltip, because "$1.5M–$2M" next to "≤ $1.5M"
 * otherwise looks like the two overlap at exactly $1,500,000, and which side a loan of
 * that size lands on is a real question with a real answer.
 */
const bandLabel = (min: number, max: number): string =>
  (min <= 0 ? `≤ ${shortMoney(max)}` : `${shortMoney(min)}–${shortMoney(max)}`);

const bandTitle = (min: number, max: number): string =>
  (min <= 0
    ? `Loan amount up to and including ${exactMoney(max)}`
    : `Loan amount above ${exactMoney(min)}, up to and including ${exactMoney(max)}`);

/**
 * A FICO rung. 850 is the top of the scale and 0 is a real value, not a blank: several
 * matrices publish a NO CREDIT SCORE grid and a 0-850 rung is a grid with no FICO axis
 * at all, which must not render as "0+".
 */
function ficoLabel(min: number, max: number): string {
  if (min <= 0 && max >= 850) return 'Any score';
  if (min <= 0) return `≤ ${max}`;
  if (max >= 850) return `${min}+`;
  return `${min}–${max}`;
}

const pct = (n: number): string => `${parseFloat(n.toFixed(2))}%`;

/** Codes off the wire are plain strings; the shared label maps fall back to the code. */
const docLabel = (c: string): string => docTypeLabel(c as DocTypeCode);
const occLabel = (c: string): string => occupancyLabel(c as Occupancy);
const purLabel = (c: string): string => purposeLabel(c as LoanPurpose);
/** 'all' is a matrix_cell value with no vocabulary entry - it means the grid has no
 *  property-type axis, which is a statement worth making in words. */
const propLabel = (c: string): string =>
  (c === 'all' ? 'All property types' : propertyLabel(c as PropertyType));

/* ----------------------------------------------------------------- the grid */

const cellKey = (c: { ficoMin: number; ficoMax: number; loanMin: number; loanMax: number }) =>
  `${c.ficoMin}:${c.ficoMax}|${c.loanMin}:${c.loanMax}`;

/**
 * Turn a flat cell list into the rectangle the lender printed.
 *
 * Rows descend by FICO and columns ascend by loan size, which is the orientation every
 * matrix in the corpus uses, so the panel reads the way the PDF beside it reads.
 *
 * The rectangle is frequently RAGGED and that is not an extraction fault. Arc's Edge DSCR
 * purchase grid offers three loan bands at 700+ and only the smallest band at 680-699,
 * because the lender does not lend that big to that score. So a coordinate with no cell
 * is a third state - not a number, and not the lender's em-dash either - and the three
 * are drawn differently on purpose. Migration 003 makes the same argument about
 * `max_ltv IS NULL`: reporting a gap in our data as a decline by the lender is the
 * fastest way to lose a broker's trust, and reporting a decline as a gap is worse.
 */
function layout(cells: MatrixCell[]): {
  ficoRows: { min: number; max: number }[];
  loanCols: { min: number; max: number }[];
  at: Map<string, MatrixCell>;
} {
  const ficoSeen = new Map<string, { min: number; max: number }>();
  const loanSeen = new Map<string, { min: number; max: number }>();
  const at = new Map<string, MatrixCell>();
  for (const c of cells) {
    ficoSeen.set(`${c.ficoMin}:${c.ficoMax}`, { min: c.ficoMin, max: c.ficoMax });
    loanSeen.set(`${c.loanMin}:${c.loanMax}`, { min: c.loanMin, max: c.loanMax });
    at.set(cellKey(c), c);
  }
  return {
    ficoRows: [...ficoSeen.values()].sort((a, b) => b.min - a.min),
    loanCols: [...loanSeen.values()].sort((a, b) => a.max - b.max),
    at,
  };
}

/** Everything the tooltip can say about one cell that the cell itself has no room for. */
function cellTitle(c: MatrixCell): string {
  const parts: string[] = [];
  parts.push(c.maxLtv === null
    ? 'Printed as a dash on the matrix: not offered at this combination'
    : `Max LTV ${pct(c.maxLtv)}`);
  if (c.maxCltv !== null) parts.push(`Max CLTV ${pct(c.maxCltv)}`);
  if (c.minReservesMonths !== null) parts.push(`Reserves ${c.minReservesMonths} months`);
  if (c.maxDti !== null) parts.push(`Max DTI ${pct(c.maxDti)}`);
  return parts.join(' · ');
}

function Grid({ grid, showDscr }: { grid: MatrixGrid; showDscr: boolean }) {
  const { ficoRows, loanCols, at } = layout(grid.cells);
  const anyNotOffered = grid.cells.some((c) => c.maxLtv === null);
  const anyGap = ficoRows.length * loanCols.length > grid.cells.length;

  return (
    <div style={{ marginTop: 14 }}>
      {showDscr ? (
        <div className="lbl" style={{ marginBottom: 6 }}>
          {grid.dscrMin === null ? 'No DSCR floor' : `DSCR ${grid.dscrMin.toFixed(2)} and above`}
        </div>
      ) : null}

      {/*
        The ONLY sideways scroller on the page. A wide grid scrolls inside this box; the
        document body never moves. The FICO column is sticky inside it, so scrolling to
        the $3M band does not leave a row of numbers with nothing saying which score they
        belong to - which is the exact failure that makes a scrolling table useless.
      */}
      <div className="mtx-scroll">
        <table className="mtx">
          <thead>
            <tr>
              <th scope="col" className="mtx-rowhead mtx-corner">FICO</th>
              {loanCols.map((b) => (
                <th key={`${b.min}:${b.max}`} scope="col" title={bandTitle(b.min, b.max)}>
                  {bandLabel(b.min, b.max)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ficoRows.map((f) => (
              <tr key={`${f.min}:${f.max}`}>
                <th scope="row" className="mtx-rowhead">{ficoLabel(f.min, f.max)}</th>
                {loanCols.map((b) => {
                  const c = at.get(`${f.min}:${f.max}|${b.min}:${b.max}`);
                  if (!c) {
                    return (
                      <td key={`${b.min}:${b.max}`} className="mtx-gap"
                          title="No row on this grid — the lender publishes nothing here">
                        <span aria-hidden="true"> </span>
                        <span className="vh">no row published</span>
                      </td>
                    );
                  }
                  return (
                    <td
                      key={`${b.min}:${b.max}`}
                      className={c.isCited ? 'mtx-cited' : undefined}
                      {...(c.isCited ? { 'aria-current': 'true' as const } : {})}
                      title={cellTitle(c)}
                    >
                      {c.maxLtv === null
                        ? <span className="mtx-off">—</span>
                        : <span className="mtx-ltv">{pct(c.maxLtv)}</span>}
                      {c.maxCltv !== null && c.maxCltv !== c.maxLtv ? (
                        <div className="mtx-sub">CLTV {pct(c.maxCltv)}</div>
                      ) : null}
                      {c.isCited ? <span className="vh"> — the cited cell</span> : null}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/*
        A legend for the two empty states, and only for the ones this grid actually
        contains. A permanent key under a full rectangle is noise that trains the eye to
        skip the line on the one grid where it matters.
      */}
      {anyNotOffered || anyGap ? (
        <div className="mtx-legend">
          {anyNotOffered ? <span><b>—</b> the lender prints a dash: not offered</span> : null}
          {anyGap ? <span><b className="mtx-key-gap" /> no row published at this combination</span> : null}
        </div>
      ) : null}
    </div>
  );
}

/* ---------------------------------------------------------------- the prose */

/** What a section does to the grid, said in four words rather than as an enum. */
const POLARITY: Record<string, { word: string; cls: string }> = {
  restricts:     { word: 'narrows the grid', cls: 'pill-cond' },
  ineligible:    { word: 'rules cases out',  cls: 'pill-no' },
  permits:       { word: 'widens the grid',  cls: 'pill-yes' },
  informational: { word: 'context',          cls: 'pill-unk' },
};

/**
 * A section is open by default when it can change the number above it.
 *
 * Anything that restricts, rules out, or carries carve-outs is the reason this pairing
 * was asked for - Arc's footnote capping cash-in-hand at $1,000,000 above 65% LTV is
 * precisely a rule the grid does not contain. "Amortization Type" is not, and a page can
 * carry thirty of those, so the merely informational ones arrive collapsed rather than
 * pushing the qualifying one below the fold.
 */
const opensByDefault = (s: MatrixProse): boolean =>
  s.rulePolarity === 'restricts' || s.rulePolarity === 'ineligible' || s.hasCarveouts;

function PolarityTag({ polarity }: { polarity: string | null }) {
  const p = POLARITY[polarity ?? 'informational'] ?? POLARITY['informational']!;
  return <span className={`pill ${p.cls}`} style={{ fontSize: 10 }}>{p.word}</span>;
}

function Prose({ sections, showBodies }: { sections: MatrixProse[]; showBodies: boolean }) {
  if (sections.length === 0) return null;
  const qualifying = sections.filter(opensByDefault).length;

  return (
    <div style={{ marginTop: 18 }}>
      <div className="lbl">
        Printed on the same page{sections.length > 1 ? ` · ${sections.length} sections` : ''}
      </div>

      {showBodies ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {sections.map((s) => (
            <details key={s.id} className="mtx-sec" open={opensByDefault(s)}>
              <summary>
                <span className="mtx-sec-title">
                  {s.sectionNumber ? `§${s.sectionNumber} ` : ''}{s.title}
                </span>
                <PolarityTag polarity={s.rulePolarity} />
                <span className="mtx-sec-page">
                  p.{s.pageStart}{s.pageEnd !== s.pageStart ? `–${s.pageEnd}` : ''}
                </span>
              </summary>
              {/*
                Rendered as plain text with the line breaks kept, not parsed as markdown.
                These bodies are the lender's own wording including their numbering and
                their bullets; a markdown pass would silently reflow "1." lists the
                lender cross-references by number, and there is no renderer in this app
                to do it with anyway.
              */}
              <div className="mtx-sec-body">{s.bodyMd}</div>
            </details>
          ))}
        </div>
      ) : (
        /*
          The broker's version of the same information: that the fine print exists, what
          each piece of it does to the grid, and what to do about it. See the boundary
          argument at the top of this file for why the words themselves stop here.
        */
        <>
          <ul className="mtx-sec-list">
            {sections.map((s) => (
              <li key={s.id}>
                <span className="mtx-sec-title">{s.title}</span>
                {' '}<PolarityTag polarity={s.rulePolarity} />
              </li>
            ))}
          </ul>
          <div className="mtx-note">
            {qualifying > 0
              ? `${qualifying === 1 ? 'One of these' : `${qualifying} of these`} qualifies the grid above — a footnote can cap or cut an LTV the table prints. `
              : ''}
            The answer on this page was written with all of {sections.length === 1 ? 'it' : 'them'} read
            in full. Ask a follow-up question here to have {sections.length === 1 ? 'it' : 'them'} applied
            to your file, or ask the lender&apos;s AE for the product matrix itself.
          </div>
        </>
      )}
    </div>
  );
}

/* --------------------------------------------------------------- the panel */

export default function MatrixPanel({ slice, sourceViewable }: Props) {
  const showBodies = sourceViewable || !PROSE_IS_SOURCE_MATERIAL;
  const s = slice.slice;

  // Nothing to add. The citation panel above already stands on its own, so an empty
  // heading over an empty box would be strictly worse than not rendering at all.
  if (!s && slice.sections.length === 0) return null;

  return (
    <section className="mtx-panel" aria-label="The matrix behind this citation">
      {s ? (
        <>
          <div className="lbl">The grid this number is read off</div>
          <div className="serif" style={{ fontSize: 16 }}>{s.programName}</div>
          {/*
            The coordinates held fixed, stated rather than implied. A grid with no caption
            is a table of numbers that could be about any loan; naming the axes it does
            NOT vary over is what makes "the whole matrix that APPLIES" a checkable claim
            instead of a boast.

            The slice actually holds NINE coordinates fixed, not the five drawn here. Two
            of the others - credit tier and borrower status - were invisible until the
            server started sending them, and their absence was a real defect rather than
            a tidiness one: Deephaven prints Standard and Recent Event as two separate
            grids, and 153 slices are split by citizenship. A broker was shown the right
            sub-table under a caption describing a different one.

            They are drawn only when they are not '*'. Almost every grid is unsplit on
            both, and a caption reading "standard · any citizenship" on all of them would
            be noise that trains the eye to skip the line - which is how the two that
            MATTER would go unread. Document version is the ninth and is never drawn: it
            is which edition we hold, not a property of the loan.

            The investor is not among them for a broker. Results.tsx makes this argument
            in full: a channel is presented as one of the lender's own programs, because
            "delivered to TransAm" describes our supply chain to somebody trying to place
            a loan and names a desk they cannot call.
          */}
          <div className="mtx-axes">
            <span title="Documentation type">{docLabel(s.docTypeCode)}</span>
            <span title="Occupancy">{occLabel(s.occupancy)}</span>
            <span title="Transaction type">{purLabel(s.purpose)}</span>
            <span title="Property type">{propLabel(s.propertyType)}</span>
            {s.creditTier && s.creditTier !== '*' && s.creditTier !== 'standard' ? (
              <span title="Credit tier — this grid is one of several the lender splits by credit history">
                {s.creditTier.replace(/_/g, ' ')}
              </span>
            ) : null}
            {s.borrowerStatus && s.borrowerStatus !== '*' ? (
              <span title="Borrower status — this grid is one of several the lender splits by citizenship">
                {s.borrowerStatus.replace(/_/g, ' ')}
              </span>
            ) : null}
            {sourceViewable && !s.isBaseChannel ? (
              <span title="Delivery channel — staff view only">{s.investorName}</span>
            ) : null}
          </div>

          {s.grids.map((g, i) => (
            <Grid key={g.dscrMin ?? `none-${i}`} grid={g} showDscr={s.grids.length > 1} />
          ))}
        </>
      ) : null}

      <Prose sections={slice.sections} showBodies={showBodies} />
    </section>
  );
}
